import { supabase } from "./supabase";
import { getProductById } from "./productService";

/**
 * Cart and Wishlist stored in Supabase (replaces localStorage).
 * Requires authenticated user (email, Google, or anonymous).
 */

function rowToCartItem(row) {
  return {
    id: row.product_id,
    name: row.product_name,
    price: Number(row.product_price),
    img: row.product_img,
    link: row.product_link || "/jewellery",
    qty: row.qty || 1,
  };
}

function rowToWishItem(row) {
  return {
    id: row.product_id,
    name: row.product_name,
    price: Number(row.product_price),
    img: row.product_img,
    link: row.product_link || "/jewellery",
  };
}

function notifyCart() {
  window.dispatchEvent(new Event("cart_updated"));
}

function notifyWishlist() {
  window.dispatchEvent(new Event("wishlist_updated"));
}

export async function getCart(userId) {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from("cart_items")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: true });

    if (error) {
      console.warn("getCart error:", error);
      return [];
    }

    // Sync stale cart row data with live DB prices and details
    const liveCartItems = await Promise.all((data || []).map(async (row) => {
      const liveProd = await getProductById(row.product_id);
      
      // If live product exists, use its fresh price (offerPrice usually), Name, and Image
      if (liveProd) {
        return {
          id: row.product_id,
          name: liveProd.title || row.product_name,
          price: Number(liveProd.offerPrice ?? liveProd.price ?? row.product_price),
          img: liveProd.images?.[0] || row.product_img,
          link: liveProd.slug ? `/product/${liveProd.slug}` : (row.product_link || "/jewellery"),
          qty: row.qty || 1,
          weight: liveProd.weight || 0.3,
          stockQuantity: liveProd.stockQuantity || 0,
        }
      }

      // Fallback if product was deleted from DB
      return rowToCartItem(row);
    }));

    return liveCartItems;

  } catch (err) {
    console.warn("getCart error:", err);
    return [];
  }
}

export async function addToCart(userId, item) {
  if (!userId) return [];
  try {
    const { data: existing } = await supabase
      .from("cart_items")
      .select("id, qty")
      .eq("user_id", userId)
      .eq("product_id", item.id)
      .maybeSingle();

    if (existing) {
      await supabase
        .from("cart_items")
        .update({ qty: (existing.qty || 1) + 1 })
        .eq("id", existing.id);
    } else {
      await supabase.from("cart_items").insert({
        user_id: userId,
        product_id: item.id,
        product_name: item.name,
        product_price: item.price ?? 0,
        product_img: item.img ?? null,
        product_link: item.link ?? null,
        qty: 1,
      });
    }
    notifyCart();
    return getCart(userId);
  } catch (err) {
    console.warn("addToCart error:", err);
    return [];
  }
}

export async function setCartQty(userId, productId, qty) {
  if (!userId) return [];
  try {
    if (qty <= 0) {
      await supabase
        .from("cart_items")
        .delete()
        .eq("user_id", userId)
        .eq("product_id", productId);
    } else {
      const { data: existing } = await supabase
        .from("cart_items")
        .select("id")
        .eq("user_id", userId)
        .eq("product_id", productId)
        .maybeSingle();

      if (existing) {
        await supabase
          .from("cart_items")
          .update({ qty })
          .eq("id", existing.id);
      }
    }
    notifyCart();
    return getCart(userId);
  } catch (err) {
    console.warn("setCartQty error:", err);
    return [];
  }
}

export async function updateCartQty(userId, productId, delta) {
  if (!userId) return [];
  try {
    const { data: row } = await supabase
      .from("cart_items")
      .select("id, qty")
      .eq("user_id", userId)
      .eq("product_id", productId)
      .maybeSingle();

    if (!row) return getCart(userId);
    const newQty = Math.max(0, (row.qty || 1) + delta);

    if (newQty <= 0) {
      await supabase.from("cart_items").delete().eq("id", row.id);
    } else {
      await supabase.from("cart_items").update({ qty: newQty }).eq("id", row.id);
    }
    notifyCart();
    return getCart(userId);
  } catch (err) {
    console.warn("updateCartQty error:", err);
    return [];
  }
}

export async function removeFromCart(userId, productId) {
  if (!userId) return [];
  try {
    await supabase
      .from("cart_items")
      .delete()
      .eq("user_id", userId)
      .eq("product_id", productId);
    notifyCart();
    return getCart(userId);
  } catch (err) {
    console.warn("removeFromCart error:", err);
    return [];
  }
}

export async function clearCart(userId) {
  if (!userId) return [];
  try {
    await supabase.from("cart_items").delete().eq("user_id", userId);
    notifyCart();
    return [];
  } catch (err) {
    console.warn("clearCart error:", err);
    return [];
  }
}

export async function setCart(userId, items) {
  if (!userId) return [];
  try {
    await supabase.from("cart_items").delete().eq("user_id", userId);
    if (items?.length > 0) {
      await supabase.from("cart_items").insert(
        items.map((it) => ({
          user_id: userId,
          product_id: it.id,
          product_name: it.name,
          product_price: it.price ?? 0,
          product_img: it.img ?? null,
          product_link: it.link ?? null,
          qty: it.qty ?? 1,
        }))
      );
    }
    notifyCart();
    return getCart(userId);
  } catch (err) {
    console.warn("setCart error:", err);
    return [];
  }
}

// ---------- Wishlist ----------

export async function getWishlist(userId) {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from("wishlist_items")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.warn("getWishlist error:", error);
      return [];
    }
    
    // Sync stale wishlist data with live DB
    const liveWishlistItems = await Promise.all((data || []).map(async (row) => {
      const liveProd = await getProductById(row.product_id);
      
      if (liveProd) {
        return {
          id: row.product_id,
          name: liveProd.title || row.product_name,
          price: Number(liveProd.offerPrice ?? liveProd.price ?? row.product_price),
          img: liveProd.images?.[0] || row.product_img,
          link: liveProd.slug ? `/product/${liveProd.slug}` : (row.product_link || "/jewellery"),
        }
      }

      return rowToWishItem(row);
    }));

    return liveWishlistItems;

  } catch (err) {
    console.warn("getWishlist error:", err);
    return [];
  }
}

export async function toggleWishlist(userId, item) {
  if (!userId) return [];
  try {
    const { data: existing } = await supabase
      .from("wishlist_items")
      .select("id")
      .eq("user_id", userId)
      .eq("product_id", item.id)
      .maybeSingle();

    if (existing) {
      await supabase.from("wishlist_items").delete().eq("id", existing.id);
    } else {
      await supabase.from("wishlist_items").insert({
        user_id: userId,
        product_id: item.id,
        product_name: item.name,
        product_price: item.price ?? 0,
        product_img: item.img ?? null,
        product_link: item.link ?? null,
      });
    }
    notifyWishlist();
    return getWishlist(userId);
  } catch (err) {
    console.warn("toggleWishlist error:", err);
    return [];
  }
}

export async function removeFromWishlist(userId, productId) {
  if (!userId) return [];
  try {
    await supabase
      .from("wishlist_items")
      .delete()
      .eq("user_id", userId)
      .eq("product_id", productId);
    notifyWishlist();
    return getWishlist(userId);
  } catch (err) {
    console.warn("removeFromWishlist error:", err);
    return [];
  }
}

export async function clearWishlist(userId) {
  if (!userId) return [];
  try {
    await supabase.from("wishlist_items").delete().eq("user_id", userId);
    notifyWishlist();
    return [];
  } catch (err) {
    console.warn("clearWishlist error:", err);
    return [];
  }
}
